﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";
import { createUseStyles } from "react-jss";
import { useIntl } from "~base/Internationalization/IntlContext";
import type { SoPNLConsilioTheme } from "~jrad/Styling/SoPNLConsilioTheme";
import type { SelfServiceViewAction } from "../Store";
import type { SelfServiceViewState } from "../Types";
import Grid from "~base/Components/Layout/Grid";
import TitleText from "~base/Components/Primitives/TitleText";
import TitleDate from "~base/Components/Primitives/TitleDate";
import SelfServicePageNavigationControls from "../ChildComponents/PageNavigationControls";
import { dateIsOlderThanXYears, parseSysDate } from "~base/Types/DateTime";
import FieldError from "../ChildComponents/FieldError";
import { useValidation } from "~base/Validation/ValidationContext";
import { useSettings } from "~base/Settings/SettingsContext";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

export interface SelfServicePageClaimsProps {
    isLoading: boolean;
    state: SelfServiceViewState;
    dispatch: React.Dispatch<SelfServiceViewAction>;
    postRegistrationRequest: () => void;
}

const useStyles = createUseStyles((theme: SoPNLConsilioTheme) => ({
    container: {},
}));

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const PageClaims: React.FunctionComponent<SelfServicePageClaimsProps> = ({
    isLoading,
    state,
    dispatch,
    postRegistrationRequest,
}) => {
    const intl = useIntl();
    const classes = useStyles();
    const validation = useValidation();
    const settings = useSettings();

    const minAllowedAge = Number(
        settings.valueAsString("selfService.minUserAge")
    );
    const maxAllowedAge = Number(
        settings.valueAsString("selfService.maxUserAge")
    );

    const validateBirthDate = (strDate: string): boolean => {
        if (!strDate || !validation.date(strDate)) {
            return false;
        }

        const date = intl.parseDate(strDate);

        return (
            dateIsOlderThanXYears(date, minAllowedAge) &&
            !dateIsOlderThanXYears(date, maxAllowedAge)
        );
    };

    const isBirthDateValid = validateBirthDate(
        !!state.requestData.claims.birthDate
            ? intl.formatDateShort(state.requestData.claims.birthDate)
            : ""
    );

    const areFieldsValid =
        !!state.requestData.claims.firstName?.trim() &&
        !!state.requestData.claims.lastName?.trim() &&
        !!state.requestData.claims.birthDate &&
        isBirthDateValid;

    return (
        <>
            <Grid className={classes.container} columns={3}>
                <div>
                    <TitleText
                        testIdentifier="VisiFinance.UI.SelfServiceRegister.FirstName"
                        titleTextCode="VisiFinance.UI.SelfServiceRegister.FirstName"
                        value={state.requestData.claims.firstName}
                        setValue={(value) => {
                            dispatch({
                                type: "setRequestDataClaim",
                                key: "firstName",
                                value,
                            });
                        }}
                    />
                    <FieldError message={state.responseError.firstName} />
                </div>
                <div>
                    <TitleText
                        testIdentifier="VisiFinance.UI.SelfServiceRegister.LastName"
                        titleTextCode="VisiFinance.UI.SelfServiceRegister.LastName"
                        value={state.requestData.claims.lastName}
                        setValue={(value) => {
                            dispatch({
                                type: "setRequestDataClaim",
                                key: "lastName",
                                value,
                            });
                        }}
                    />
                    <FieldError message={state.responseError.lastName} />
                </div>
                <div>
                    <TitleDate
                        testIdentifier="VisiFinance.UI.SelfServiceRegister.Birthdate"
                        titleTextCode="VisiFinance.UI.SelfServiceRegister.Birthdate"
                        value={state.requestData.claims.birthDate}
                        setValue={(value) => {
                            dispatch({
                                type: "setRequestDataClaim",
                                key: "birthDate",
                                value,
                            });
                        }}
                        isValid={validateBirthDate}
                    />
                    <FieldError
                        message={
                            (!!state.requestData.claims.birthDate &&
                                !isBirthDateValid &&
                                intl.formatText(
                                    "VisiFinance.UI.SelfServiceRegister.BirthdateInvalid"
                                )) ||
                            state.responseError.birthDate
                        }
                    />
                </div>
            </Grid>
            <SelfServicePageNavigationControls
                state={state}
                dispatch={dispatch}
                postRegistrationRequest={postRegistrationRequest}
                disablePrev={isLoading}
                disableNext={!areFieldsValid || isLoading}
            />
        </>
    );
};

export default PageClaims;
